"use client";

import { useEffect, useState } from "react";

interface CountdownTimerProps {
  targetDate: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function CountdownTimer({ targetDate }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +new Date(targetDate) - +new Date();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const timeUnits = [
    { label: "DAYS", value: timeLeft.days },
    { label: "HOURS", value: timeLeft.hours },
    { label: "MINUTES", value: timeLeft.minutes },
    { label: "SECONDS", value: timeLeft.seconds },
  ];

  return (
    <div className="flex flex-wrap justify-center gap-4 md:gap-6">
      {timeUnits.map((unit, index) => (
        <div
          key={unit.label}
          className="flex flex-col items-center animate-in fade-in slide-in-from-bottom-4 duration-700"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-primary-foreground/20 bg-primary-foreground/10 backdrop-blur-sm md:h-28 md:w-28 transition-all duration-300 hover:scale-105 hover:bg-primary-foreground/20">
              <span className="text-3xl font-bold text-primary-foreground md:text-5xl transition-all duration-300">
                {String(unit.value).padStart(2, "0")}
              </span>
            </div>
            {unit.label === "SECONDS" && (
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-r from-secondary/20 to-accent/20 animate-pulse"></div>
            )}
          </div>
          <span className="mt-2 text-xs font-semibold uppercase tracking-wider text-primary-foreground/70 md:text-sm">
            {unit.label}
          </span>
        </div>
      ))}
    </div>
  );
}
